import { createContext, useState, useContext, useEffect, useCallback, useRef } from "react";

import { LoaderSearchImage } from "../../components/Loader";
import { setTravelApiToken } from "../../_config/travel";
import { setApiToken } from "_config/http";
import {
    isTrustedParentMessage,
    postToParent,
    setChannelApiToken,
} from "../../_config/channelToken";

const HANDSHAKE_TIMEOUT_MS = 10000;

const EVENTS = {
    HANDSHAKE_START: 'HANDSHAKE_START',
    AUTH_DATA: 'AUTH_DATA',
    AUTH_TOKEN_UPDATE: 'AUTH_TOKEN_UPDATE',
    HANDSHAKE_COMPLETE: 'HANDSHAKE_COMPLETE',
    REDIRECT_RESULT: 'REDIRECT_RESULT',
};

const EventsContext = createContext(null);

export const EventsProvider = ({ children }) => {
    const [handshakeStatus, setHandshakeStatus] = useState('idle');
    const [authData, setAuthData] = useState(null);
    const [redirectResult, setRedirectResult] = useState(null);
    const timeoutRef = useRef(null);

    const clearRedirectResult = useCallback(() => {
        setRedirectResult(null);
    }, []);

    const sendEvent = useCallback((type, payload) => {
        if (window.parent !== window) {
            postToParent({ type, ...(payload && { payload }) });
        }
    }, []);

    useEffect(() => {
        if (window.parent === window) {
            console.warn('[EventsProvider] Não está dentro de um iframe. Handshake abortado.');
            setHandshakeStatus('ready');
            return;
        }

        const handleMessage = (event) => {
            if (!isTrustedParentMessage(event)) return;

            const { type, payload } = event.data || {};
            if (!type || !Object.values(EVENTS).includes(type)) return;

            switch (type) {
                case EVENTS.AUTH_DATA:
                case EVENTS.AUTH_TOKEN_UPDATE: {
                    const { token, apiToken, userData, balance, customer } = payload || {};
                    const finalUserData = userData || customer || event.data?.customer || {};

                    if (!apiToken && !token) {
                        console.warn('[EventsProvider] AUTH_DATA recebido sem token, ignorando.');
                        return;
                    }

                    setAuthData({ apiToken, userData: finalUserData, balance: parseInt(balance) || 0 });
                    setTravelApiToken(apiToken);
                    setApiToken(apiToken);
                    setHandshakeStatus('ready');
                    sendEvent(EVENTS.HANDSHAKE_COMPLETE);

                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                        timeoutRef.current = null;
                    }
                    break;
                }
                case EVENTS.AUTH_TOKEN_UPDATE: {
                    if (!setChannelApiToken(payload?.apiToken)) {
                        console.warn('[EventsProvider] AUTH_TOKEN_UPDATE recebido sem token de API.');
                    }
                    break;
                }
                case EVENTS.REDIRECT_RESULT: {
                    const { result_type, bannerData } = payload || {};
                    if (result_type && bannerData) {
                        setRedirectResult({ result_type, bannerData });
                    }
                    break;
                }
                default:
                    break;
            }
        };

        window.addEventListener('message', handleMessage);

        setHandshakeStatus('pending');
        sendEvent(EVENTS.HANDSHAKE_START);

        timeoutRef.current = setTimeout(() => {
            setHandshakeStatus((prev) => {
                if (prev === 'pending') {
                    console.error('[EventsProvider] Handshake timeout após', HANDSHAKE_TIMEOUT_MS, 'ms');
                    return 'error';
                }
                return prev;
            });
        }, HANDSHAKE_TIMEOUT_MS);

        return () => {
            window.removeEventListener('message', handleMessage);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [sendEvent]);

    if (handshakeStatus === 'pending' || handshakeStatus === 'idle') {
        return <LoaderSearchImage />;
    }

    if (handshakeStatus === 'error') {
        return <LoaderSearchImage />;
    }

    return (
        <EventsContext.Provider value={{ handshakeStatus, authData, sendEvent, redirectResult, clearRedirectResult }}>
            {children}
        </EventsContext.Provider>
    );
};

export const useEvents = () => useContext(EventsContext);
